import { NextPage } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import { ParsedUrlQuery } from 'querystring';
import { useLoadAnalytics } from '@sy/app-client/src/ssrAdapter/services/analytics';

import BlogTemplate from '~/components/templates/BlogTemplate';
import type { CommonContext, InferProps } from '~/getServerSideProps';
import { buildGetServerSideProps } from '~/getServerSideProps';
import { BlogPost as BlogPostPage } from '~/scenes/Blog';
import {
	getAllBlogPostTilesSortedByCreation,
	getBlogCategory,
	getBlogMainCategory,
	getBlogManifest,
	getBlogPost,
	getCategorizedBlogPostTilesSortedByCreation,
	getRelatedPosts,
} from '~/services/blog';
import type { BlogMainCategory } from '~/services/blog/categories';
import type {
	CacheBlogPost,
	CacheReducedBlogPost,
} from '~/services/contentful/model/ContentfulBlogPost';
import { Metadata } from '~/types';
import buildMetatadata from '~/utils/buildMetadata';
import BlogOverview from '../../scenes/Blog/scenes/BlogOverview';

// category start

const categorizedPostsServerSideProps = async (
	context: CommonContext<UrlParams>,
) => {
	const { locale, params, query } = context;
	const isPreview = !!query?.preview;

	const category = getBlogCategory(params?.segments?.[0]);
	const mainCategory = getBlogMainCategory(category);

	if (!category || category !== mainCategory) {
		return {
			redirect: {
				destination: '/blog',
				permanent: false,
			},
		};
	}

	return {
		props: {
			category,
			posts:
				(
					await getCategorizedBlogPostTilesSortedByCreation({
						locale,
						category,
						isPreview,
					})
				)?.filter(post => !post.unlisted) || null,
		},
	};
};

const allPostsServerSideProps = async (context: CommonContext<UrlParams>) => {
	const { locale, query } = context;
	const isPreview = !!query?.preview;

	return {
		props: {
			posts:
				(
					await getAllBlogPostTilesSortedByCreation({
						locale,
						isPreview,
					})
				)?.filter(post => !post.unlisted) || null,
			metadata: buildMetatadata({}),
		},
	};
};

const categoryServerSideProps = async (context: CommonContext<UrlParams>) => {
	const { params } = context;

	if (params?.segments?.[0]) {
		return categorizedPostsServerSideProps(context);
	}

	return allPostsServerSideProps(context);
};

// category end

const PostOrPostsGroup: NextPage<InferProps<typeof getServerSideProps>> = ({
	activeCategory,
	featureFlags,
	pageUrl,
	post,
	relatedPosts,
	user,
	//
	category,
	posts,
}) => {
	useLoadAnalytics();
	const logoVersion = featureFlags.orion.logoExperimentVariant;

	if (posts) {
		return (
			<BlogTemplate
				activeCategory={category}
				user={user}
				logoVersion={logoVersion}
			>
				<BlogOverview posts={posts} category={category} />
			</BlogTemplate>
		);
	}

	return (
		<>
			<Head>
				<meta name="robots" content="max-image-preview:large" />
			</Head>
			{post.content?.includes('https://www.instagram.com') && (
				<Script id="instagram" src="https://www.instagram.com/embed.js" />
			)}
			<BlogTemplate
				activeCategory={activeCategory}
				user={user}
				logoVersion={logoVersion}
			>
				<BlogPostPage
					post={post}
					relatedPosts={relatedPosts}
					pageUrl={pageUrl}
				/>
			</BlogTemplate>
		</>
	);
};

interface UrlParams extends ParsedUrlQuery {
	segments?: string[]; // can be either a post slug and a category
}

const customServerSideProps = async (context: CommonContext<UrlParams>) => {
	const {
		params,
		query,
		locale,
		req: {
			headers: { host },
		},
	} = context;

	// If the slug corresponds to the name of a category, we render the page for that category
	const { segments } = params || {};
	if (segments && segments.length > 1) {
		return {
			redirect: {
				destination: `/blog/${segments[segments.length - 1]}`,
				permanent: true,
			},
		};
	}

	const slug = segments?.[0];
	if (
		slug === undefined || // /blog page
		getBlogCategory(slug) // /blog/[category] page
	) {
		return categoryServerSideProps(context);
	}

	// Otherwise, this should be the page of an individual post

	const isPreview = !!query?.preview;

	const manifest = await getBlogManifest({
		locale,
		isPreview,
	});
	if (!manifest) {
		return {
			redirect: {
				destination: '/blog',
				permanent: false,
			},
		};
	}

	const post = await getBlogPost({
		locale,
		slug,
		isPreview,
		manifest,
	});

	if (!post) {
		return {
			redirect: {
				destination: '/blog',
				permanent: false,
			},
		};
	}

	const activeCategory = getBlogMainCategory(post.category);
	const relatedPosts = await getRelatedPosts({
		manifest,
		relatedPostIds: post.relatedPosts,
	});

	const pageUrl = `https://${host}/blog/${slug}`;

	return {
		props: {
			pageUrl,
			post,
			relatedPosts,
			metadata: buildMetatadata({
				title: post.title,
				description: post.summary,
				twitterImgAlt: post.coverImage?.description,
				twitterImgUrl: post.coverImage?.url,
				ogImgAlt: post.coverImage?.description,
				ogImgUrl: post.coverImage?.url,
				ogImgHeight: post.coverImage?.dimensions?.height,
				ogImgWidth: post.coverImage?.dimensions?.width,
				ogUrl: pageUrl,
				ogType: 'article',
				ogDescription: post.summary,
				articleModifiedTime: post.updatedAt,
				...(post?.firstPublishedAt
					? { articlePublishedTime: post.firstPublishedAt }
					: {}),
				articlePublisher: 'https://www.facebook.com/streamyard',
			}),
			...(activeCategory ? { activeCategory } : {}),
		},
	};
};

export default PostOrPostsGroup;

export const getServerSideProps = buildGetServerSideProps<
	| {
			category?: BlogMainCategory;
			posts: CacheReducedBlogPost[] | null;
	  }
	| {
			activeCategory?: BlogMainCategory | undefined;
			pageUrl: string;
			post: CacheBlogPost;
			relatedPosts: CacheReducedBlogPost[];
			metadata: Metadata;
	  }
>({
	customServerSideProps,
});
